import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Eye, RefreshCw, ExternalLink, Image as ImageIcon, Loader2, ChevronDown, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function ShareHistoryPage() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [shares, setShares] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const [error, setError] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/home'); return; }
    fetchShares(1, true);
  }, [user]);

  const fetchShares = async (pageNum = 1, reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/share/history?page=${pageNum}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur lors du chargement');
      const data = await res.json();
      if (reset) {
        setShares(data.shares || []);
      } else {
        setShares(prev => [...prev, ...(data.shares || [])]);
      }
      setTotal(data.total || 0);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
      if (reset) toast.error(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleResend = async (shareId) => {
    setResendingId(shareId);
    try {
      const res = await fetch(`${API_URL}/api/share/${shareId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Échec du renvoi');
      toast.success('Simulation renvoyée avec succès !');
      setShares(prev => prev.map(s =>
        s.share_id === shareId ? { ...s, resend_count: (s.resend_count || 0) + 1 } : s
      ));
    } catch (err) {
      toast.error(err.message || 'Erreur lors du renvoi');
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (share) => {
    if (share.status === 'sent' || share.status === 'success') {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-medium">Envoyé</span>;
    }
    if (share.status === 'failed' || share.error) {
      return <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">Échec</span>;
    }
    return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">En attente</span>;
  };

  const hasMore = shares.length < total;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button onClick={() => navigate('/home')} className="p-2 rounded-lg hover:bg-white/5">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide flex-1">
          Mes partages
        </h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-safe">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="spinner" />
          </div>
        ) : error && shares.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <AlertTriangle size={40} className="mb-2 opacity-30 text-red-400" />
            <p className="text-sm text-red-400 mb-3">{error}</p>
            <button onClick={() => fetchShares(1, true)} className="btn-secondary text-xs">
              Réessayer
            </button>
          </div>
        ) : shares.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
            <Send size={40} className="mb-2 opacity-30" />
            <p className="text-sm">Aucun partage pour le moment</p>
            <button
              onClick={() => navigate('/send-simulation')}
              className="mt-3 btn-secondary text-xs"
            >
              Envoyer une simulation
            </button>
          </div>
        ) : (
          <>
            {shares.map((share, i) => (
              <motion.div
                key={share.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="card p-4"
              >
                {/* Top row: date + status */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-muted-foreground">
                    {formatDate(share.created_at)}
                  </span>
                  {getStatusBadge(share)}
                </div>

                {/* Project name */}
                <p className="text-sm font-bold text-white mb-2">
                  {share.project_name || 'Projet sans nom'}
                </p>

                {/* Recipient */}
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                  <Mail size={12} />
                  <span>{share.recipient_email || share.email || '—'}</span>
                </div>

                {/* Thumbnail + resend count row */}
                <div className="flex items-center gap-3 mb-3">
                  {share.simulated_image_url ? (
                    <img
                      src={share.simulated_image_url}
                      alt="Simulation"
                      className="w-16 h-16 rounded-xl object-cover border border-white/10"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center border border-white/10">
                      <ImageIcon size={20} className="text-muted-foreground opacity-50" />
                    </div>
                  )}
                  {share.resend_count > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Renvoyé {share.resend_count} fois
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-2 border-t border-white/5">
                  <button
                    onClick={() => navigate(`/share-detail/${share.share_id}`)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-white/5 text-muted-foreground hover:text-white hover:bg-white/10 transition-all"
                  >
                    <Eye size={14} /> Détails
                  </button>
                  <button
                    onClick={() => handleResend(share.share_id)}
                    disabled={resendingId === share.share_id}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all disabled:opacity-50"
                  >
                    {resendingId === share.share_id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Renvoyer
                  </button>
                  {share.project_id && (
                    <button
                      onClick={() => navigate('/result', { state: { projectId: share.project_id } })}
                      className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-secondary/10 text-secondary hover:bg-secondary/20 transition-all"
                    >
                      <ExternalLink size={14} /> Projet
                    </button>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Load more pagination */}
            {hasMore && (
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => fetchShares(page + 1)}
                  disabled={loadingMore}
                  className="btn-secondary text-xs flex items-center gap-2"
                >
                  {loadingMore ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  Voir plus ({total - shares.length} restants)
                </button>
              </div>
            )}

            {/* Counter */}
            {!loading && shares.length > 0 && (
              <p className="text-center text-xs text-muted-foreground pt-1">
                {total} partage{total > 1 ? 's' : ''} au total
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
